/**
 * Camera shake controller.
 *
 * The camera owns a "rest" position (where main.ts wants it). On every
 * frame we apply a damped 2D offset to that rest position based on
 * accumulated trauma. `kick(intensity)` adds trauma; trauma decays
 * exponentially each tick.
 *
 * Trauma values:
 *   small  (cross-tier bounce)  ≈ 0.2
 *   medium (tier 0..3 merge)    ≈ 0.45
 *   strong (tier 4..7 merge)    ≈ 0.75
 *   ultra  (tier 8+ / win)      ≈ 1.10
 *
 * Visible offset scales with trauma² (Squirrel Eiserloh's classic curve)
 * so small kicks stay subtle and big kicks hit hard.
 */

import type { PerspectiveCamera } from "three";

export interface CameraShake {
  kick(intensity: number): void;
  tick(dt: number): void;
  detach(): void;
}

export interface CameraShakeOptions {
  camera: PerspectiveCamera;
  /** Maximum pixel-equivalent translate in world units. */
  maxOffsetWorld?: number;
  /** Decay rate per second (higher = faster recovery). */
  decayPerSec?: number;
}

const DEFAULT_MAX_OFFSET = 0.85;
const DEFAULT_DECAY = 6.0;

export function createCameraShake(
  opts: CameraShakeOptions,
): CameraShake {
  const maxOffset = opts.maxOffsetWorld ?? DEFAULT_MAX_OFFSET;
  const decayPerSec = opts.decayPerSec ?? DEFAULT_DECAY;

  // Snapshot the camera's rest position the first time tick is called.
  // We capture it lazily because main.ts moves the camera AFTER bootstrap
  // (camera.position.set is called in main.ts post-bootstrap).
  let restX: number | null = null;
  let restY: number | null = null;
  let trauma = 0;

  const kick = (intensity: number): void => {
    trauma = Math.min(1.5, trauma + intensity);
  };

  const tick = (dt: number): void => {
    if (restX === null || restY === null) {
      restX = opts.camera.position.x;
      restY = opts.camera.position.y;
    }
    if (trauma <= 0) {
      // Settle back to rest in case prior frame nudged us.
      opts.camera.position.x = restX;
      opts.camera.position.y = restY;
      return;
    }
    // Squirrel: shake = trauma². Random direction per frame.
    const shake = trauma * trauma * maxOffset;
    const angle = Math.random() * Math.PI * 2;
    opts.camera.position.x = restX + Math.cos(angle) * shake;
    opts.camera.position.y = restY + Math.sin(angle) * shake;
    trauma = Math.max(0, trauma - decayPerSec * dt);
  };

  const detach = (): void => {
    if (restX !== null && restY !== null) {
      opts.camera.position.x = restX;
      opts.camera.position.y = restY;
    }
  };

  return { kick, tick, detach };
}

export const __testing = { DEFAULT_MAX_OFFSET, DEFAULT_DECAY };
